/**
 * EliosBottomTabBar — fixed bottom navigation for mobile.
 *
 * Shows the 5 most-used pages as thumb-friendly icons (~56px tall, well
 * within the iOS HIG / Material touch target spec). Hidden on lg+ since
 * the sidebar is always visible there.
 *
 * Active tab is highlighted with the brand emerald colour. Honours the
 * iOS home-indicator safe area via env(safe-area-inset-bottom).
 */
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Phone, Calendar, BarChart3, BookUser, MessageSquare, Ticket, UserCheck,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import api from '../../lib/api';

// Two preset tab sets — keep them stable so users can rely on muscle memory.
const REAL_ESTATE_TABS = [
  { icon: LayoutDashboard, label: 'Home',     path: '/elios/dashboard' },
  { icon: Phone,           label: 'Chiamate', path: '/elios/inbound' },
  { icon: UserCheck,       label: 'Leads',    path: '/elios/leads' },
  { icon: Calendar,        label: 'Agenda',   path: '/elios/calendario' },
  { icon: BarChart3,       label: 'Analisi',  path: '/elios/analisi-ai' },
];

const CONDOMINIO_TABS = [
  { icon: LayoutDashboard, label: 'Home',     path: '/elios/dashboard' },
  { icon: Phone,           label: 'Chiamate', path: '/elios/inbound' },
  { icon: MessageSquare,   label: 'WhatsApp', path: '/elios/whatsapp' },
  { icon: Ticket,          label: 'Ticket',   path: '/elios/ticketing' },
  { icon: Calendar,        label: 'Agenda',   path: '/elios/calendario' },
];

export default function EliosBottomTabBar() {
  const location = useLocation();
  const [isCondominio, setIsCondominio] = useState(false);
  const [condominioModuleEnabled, setCondominioModuleEnabled] = useState(false);
  const [whatsappModuleEnabled, setWhatsappModuleEnabled] = useState(false);

  useEffect(() => {
    api.get('/elios-client/profile')
      .then(r => {
        setIsCondominio(r.data?.provenienza === 'condominio');
        setCondominioModuleEnabled(!!r.data?.modules?.condominio);
        setWhatsappModuleEnabled(!!r.data?.modules?.whatsapp);
      })
      .catch(() => {});
  }, []);

  const tabs = isCondominio
    ? CONDOMINIO_TABS.filter((t) => {
        if (t.path === '/elios/ticketing') return condominioModuleEnabled;
        if (t.path === '/elios/whatsapp') return whatsappModuleEnabled;
        return true;
      })
    : (condominioModuleEnabled
      ? [
          REAL_ESTATE_TABS[0],
          REAL_ESTATE_TABS[1],
          { icon: Ticket, label: 'Ticket', path: '/elios/ticketing' },
          REAL_ESTATE_TABS[3],
          REAL_ESTATE_TABS[4],
        ]
      : REAL_ESTATE_TABS);

  return (
    <nav
      data-testid="elios-bottom-tab-bar"
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.04)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="flex items-stretch justify-around">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = location.pathname === tab.path
            || (tab.path !== '/elios/dashboard' && location.pathname.startsWith(tab.path));
          return (
            <li key={tab.path} className="flex-1">
              <Link
                to={tab.path}
                data-testid={`bottom-tab-${tab.path.split('/').pop()}`}
                className={`flex flex-col items-center justify-center gap-0.5 py-2 transition-colors active:scale-[0.96] ${
                  isActive ? 'text-emerald-600' : 'text-gray-500 hover:text-gray-700'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : ''}`} strokeWidth={isActive ? 2.4 : 2} />
                <span className={`text-[10px] font-medium tracking-tight ${isActive ? 'text-emerald-700' : ''}`}>
                  {tab.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
